import {
  Box,
  Flex,
  Badge,
  Code,
  useToast,
  Divider,
  Spacer,
} from '@chakra-ui/react';
import React from 'react';

import { EditModeControls } from './EditModeControls';
import { TapeBuilderMode } from './GraphContext';
import { TapeBuilderPipelinePicker } from './pipelinePicker/TapeBuilderPipelinePicker';
import { TapeBuilderDiffDocument } from './TapeBuilderDiffDocument';
import { TapeBuilderModeSelect } from './TapeBuilderModeSelect';
import { defaultToastOptions } from './utils';
import { ViewModeControls } from './ViewModeControls';
import { GetBorrowingBasePipelineStatusQueryGQLType } from 'packages/tapeBuilder/requests/tapeBuilder.gql.generated';
import { FiRefreshCw } from 'react-icons/fi';
import { IconButton } from '~/packages/common-frontend/components/IconButton';
import { TextWithCopy } from '~/packages/common-frontend/components/TextWithCopy';
import { useCommitArtifactDocumentAndCreditAgreementsForPipelineDefinitionMutation } from '~/packages/creditAgreements/requests/requests.gql.generated';

type RenderStatusProps = {
  pipelineDefinitionId: string;
  status: GetBorrowingBasePipelineStatusQueryGQLType;
  dirty: boolean;
  updateStatus: () => void;
};
export const RenderStatus = ({
  pipelineDefinitionId,
  status,
  dirty,
  updateStatus,
}: RenderStatusProps) => {
  const toast = useToast(defaultToastOptions);

  const {
    latestArtifactDocumentId,
    latestArtifactDocumentVersion,
    wipNeedsCommit,
  } = status.borrowingBasePipelineStatus;

  const [
    commitArtifactDocumentAndCreditAgreementsMutation,
    { loading: updatingCAs },
  ] = useCommitArtifactDocumentAndCreditAgreementsForPipelineDefinitionMutation(
    {
      onCompleted: ({ commitArtifactDocumentAndCreditAgreements: result }) => {
        result.successes.forEach((success) => {
          toast({
            title: success,
            position: 'top',
            status: 'success',
            duration: null, // this is important information, do not auto-close.
          });
        });

        result.errors.forEach((errors) => {
          toast({
            title: errors,
            position: 'top',
            status: 'error',
            duration: null, // this is important information, do not auto-close.
          });
        });

        // need to reset status after commit
        updateStatus();
      },
      onError: (error) => {
        // eslint-disable-next-line no-console
        const message = `${error}`;
        toast({
          id: `${Date.now()}`,
          title: message,
          status: 'error',
          duration: null, // errors are important, do not auto-close
        });
      },
    },
  );
  return (
    <>
      {!dirty && wipNeedsCommit && (
        // should only be able to "commit" after a save.
        // if graph is "dirty", it should be saved first as wip, prior to committing (for uber-commit)
        <Badge
          ml="2"
          mr="2"
          cursor="pointer"
          variant="solid"
          colorScheme="red"
          onClick={() =>
            commitArtifactDocumentAndCreditAgreementsMutation({
              variables: { pipelineDefinitionId },
            })
          }
        >
          COMMIT TAPE-BUILDER AND UPDATE CAs
        </Badge>
      )}
      {updatingCAs && (
        <Badge ml="2" colorScheme="yellow">
          Updating CAs...
        </Badge>
      )}
      <Spacer />
      <Code ml="2" mr="2">
        Latest:
        <TextWithCopy label="Copy ArtifactDocumentId">
          {latestArtifactDocumentId}
        </TextWithCopy>
        :{latestArtifactDocumentVersion}
      </Code>
    </>
  );
};

type TapeBuilderStatusProps = {
  dirty: boolean;
  dbMessage: string | null;
  status: GetBorrowingBasePipelineStatusQueryGQLType | undefined;
  pipelineDefinitionId: string;
  mode: TapeBuilderMode;
  setMode: (mode: TapeBuilderMode) => void;
  onSaveWip: () => void;
  updateStatus: () => void;
  diff: TapeBuilderDiffDocument | null;
  setDiff: (diff: TapeBuilderDiffDocument | null) => void;
  setArtifactDocumentId: (artifactDocumentId: string) => void;
  onLoadArtifactInstances: (pipelineRunId: string) => void;
  // Edit mode controls
  onClearGraph?: () => void;
  onMigrateXlsx2Csv?: () => void;
  onResetToLatest?: () => void;
  onUndoChanges?: () => void;
  isGraphFiltered?: boolean;
};
export const TapeBuilderStatus = ({
  dirty,
  status,
  pipelineDefinitionId,
  dbMessage,
  mode,
  onSaveWip,
  updateStatus,
  diff,
  setDiff,
  onLoadArtifactInstances,
  setArtifactDocumentId,
  setMode,
  onClearGraph,
  onMigrateXlsx2Csv,
  onResetToLatest,
  onUndoChanges,
  isGraphFiltered = false,
}: TapeBuilderStatusProps) => {
  return (
    <Box width="100%" border="1px solid #ccc" id="tape-builder-status-bar">
      <Flex minWidth="max-content" alignItems="center" gap="2">
        {/* mode can be switched only when not dirty */}
        {!dirty && <TapeBuilderModeSelect mode={mode} setMode={setMode} />}
        {mode === 'Edit' &&
          onClearGraph &&
          onMigrateXlsx2Csv &&
          onResetToLatest &&
          onUndoChanges && (
            <EditModeControls
              onClearGraph={onClearGraph}
              onMigrateXlsx2Csv={onMigrateXlsx2Csv}
              onResetToLatest={onResetToLatest}
              onUndoChanges={onUndoChanges}
              filtered={isGraphFiltered}
            />
          )}
        {mode === 'Monitor' && (
          <TapeBuilderPipelinePicker
            pipelineDefinitionId={pipelineDefinitionId}
            loadArtifactInstances={onLoadArtifactInstances}
          />
        )}
        {mode === 'View' && (
          <ViewModeControls
            pipelineDefinitionId={pipelineDefinitionId}
            onDocumentSelected={(v) => setArtifactDocumentId(v)}
            diff={diff}
            setDiff={setDiff}
          />
        )}
        {dirty && (
          <Badge
            ml="2"
            mr="2"
            cursor="pointer"
            variant="solid"
            colorScheme="red"
            onClick={() => onSaveWip()}
          >
            Unsaved. Click to Save
          </Badge>
        )}
        <Divider orientation="vertical" />
        {dbMessage && (
          <Badge ml="2" variant="outline" colorScheme="yellow">
            {dbMessage}
          </Badge>
        )}
        {status && (
          <RenderStatus
            pipelineDefinitionId={pipelineDefinitionId}
            status={status}
            dirty={dirty}
            updateStatus={updateStatus}
          />
        )}
        <IconButton
          label="Refresh"
          ariaLabel="Refresh"
          icon={<FiRefreshCw />}
          variant="outline"
          onClick={updateStatus}
        />
      </Flex>
    </Box>
  );
};
